import React, { useEffect } from 'react';

import { LoadingLogo } from '@dec/shared';
import Env from 'env';
import { type User } from 'oidc-client-ts';

import auth from '../auth';

import {
  clearLocalAuthSession,
  getAuthSession,
  hydrateAuthSession,
  setAuthLoading,
  useAuthSession
} from './authSession';
import { useSessionIdleCoordinator } from './sessionIdle';
import { SessionIdleModal } from './sessionIdleModal';

interface Props {
  children?: any;
}

const SessionIdleBridge = ({ session }: { session: ReturnType<typeof useAuthSession> }) => {
  const idle = useSessionIdleCoordinator(session.user?.context?.sessionLifetime, true, {
    lifecycleKey: session.user?.context?.userProfile?.id,
    ...(Env.sessionIdleTimeoutSeconds ? { timeoutSeconds: Env.sessionIdleTimeoutSeconds } : {})
  });
  if (!idle) return null;
  return (
    <SessionIdleModal
      open={idle.prompted}
      remainingSeconds={idle.remainingSeconds}
      onContinue={idle.continueSession}
      onSignOut={idle.signOut}
    />
  );
};

export const OidcProvider = (props: Props) => {
  const session = useAuthSession();
  const isCallbackRoute = window.location.pathname === '/auth/callback';

  const onUserLoaded = (user: User) => {
    if (getAuthSession().status === 'logging-out') return;
    void hydrateAuthSession(user);
  };
  // event callback when silent renew errored
  const onSilentRenewError = (error: any) => {
    // dispatch.dispatch(silentRenewError(error));
    console.log(error, 'onSilentRenewError');
  };

  // event callback when the access token expired
  const onAccessTokenExpired = () => {
    //dispatch(userExpired());
    console.log('onAccessTokenExpired');
  };

  // event callback when the user is logged out
  const onUserUnloaded = () => {
    if (getAuthSession().status !== 'logging-out') clearLocalAuthSession();
    console.log('onUserUnloaded');
  };

  // event callback when the user is expiring
  const onAccessTokenExpiring = () => {
    //dispatch(userExpiring());
    console.log('onAccessTokenExpiring');
  };

  // event callback when the user is signed out
  const onUserSignedOut = () => {
    if (getAuthSession().status !== 'logging-out') clearLocalAuthSession();
    console.log('onUserSignedOut');
  };
  useEffect(() => {
    if (isCallbackRoute) {
      setAuthLoading(false);
      return;
    }

    const users = async () => {
      const user = await auth.userManager.getUser();

      if (user && !user.expired) {
        onUserLoaded(user);
      } else {
        clearLocalAuthSession();
      }
    };
    users();

    auth.userManager.events.addUserLoaded(onUserLoaded);
    auth.userManager.events.addSilentRenewError(onSilentRenewError);
    auth.userManager.events.addAccessTokenExpired(onAccessTokenExpired);
    auth.userManager.events.addUserUnloaded(onUserUnloaded);
    auth.userManager.events.addAccessTokenExpiring(onAccessTokenExpiring);
    auth.userManager.events.addUserSignedOut(onUserSignedOut);
    return () => {
      auth.userManager.events.removeUserLoaded(onUserLoaded);
      auth.userManager.events.removeSilentRenewError(onSilentRenewError);
      auth.userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
      auth.userManager.events.removeUserUnloaded(onUserUnloaded);
      auth.userManager.events.removeAccessTokenExpiring(onAccessTokenExpiring);
      auth.userManager.events.removeUserSignedOut(onUserSignedOut);
    };
  }, [isCallbackRoute]);

  if (session.loading) {
    return <LoadingLogo src={`${Env.publicUrl}/images/poss/logov.png`} alt="DEC loading logo" />;
  }

  return (
    <>
      {React.Children.only(props.children)}
      {(session.status === 'authenticated' || session.status === 'logging-out') && (
        <SessionIdleBridge session={session} />
      )}
    </>
  );
};
